
"use client";

import type { Options } from 'react-markdown';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import React, { useRef, useState, useEffect, useCallback } from 'react';
import { cn } from '@/lib/utils';
import { slugify } from '@/lib/utils';
import CodeBlock from './CodeBlock';
import Callout, { isCalloutMarker, stripCalloutMarker } from './Callout';
import MarkdownRechartsChart, { type MarkdownChartSpec } from './MarkdownRechartsChart';

export interface Heading {
  level: number;
  text: string;
  slug: string;
}

interface MarkdownRendererProps {
  content: string;
  onHeadingsExtracted?: (headings: Heading[]) => void;
}

const ResponsiveTable: React.FC<React.HTMLAttributes<HTMLTableElement>> = ({ children, ...props }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const tableRef = useRef<HTMLTableElement>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  const checkOverflow = useCallback(() => {
    if (tableRef.current && containerRef.current) {
      const currentScrollWidth = tableRef.current.scrollWidth;
      const currentClientWidth = containerRef.current.clientWidth;
      const HYSTERESIS_PX = 10;

      setIsOverflowing(prevIsOverflowing => {
        if (prevIsOverflowing) {
          if (currentScrollWidth < (currentClientWidth - HYSTERESIS_PX)) {
            return false;
          }
          return true;
        } else {
          if (currentScrollWidth > currentClientWidth) {
            return true;
          }
          return false;
        }
      });
    }
  }, []);

  useEffect(() => {
    checkOverflow();
    const containerEl = containerRef.current;
    if (!containerEl) return;

    const observer = new ResizeObserver(checkOverflow);
    observer.observe(containerEl);

    return () => observer.unobserve(containerEl);
  }, [checkOverflow]);

  return (
    <div ref={containerRef} className="overflow-x-auto w-full">
      <table ref={tableRef} className={cn(props.className, isOverflowing ? 'table-condensed' : '')} {...props}>
        {children}
      </table>
    </div>
  );
};


export default function MarkdownRenderer({ content, onHeadingsExtracted }: MarkdownRendererProps) {
  useEffect(() => {
    if (onHeadingsExtracted) {
      const headingRegex = /^(##)\s+(.*)/gm;
      const headings = Array.from(content.matchAll(headingRegex)).map(match => ({
        level: match[1].length,
        text: match[2].trim(),
        slug: slugify(match[2].trim())
      }));
      onHeadingsExtracted(headings);
    }
  }, [content, onHeadingsExtracted]);

  const components: Options["components"] = {
    h2: ({ node, ...props }) => {
        const text = React.Children.toArray(props.children).join('');
        const slug = slugify(text);
        return <h2 id={slug} {...props} />;
    },
    pre({ children, ...props }) {
      const child = React.Children.toArray(children)[0];
      if (React.isValidElement(child) && child.props) {
        const codeEl = child.props as { className?: string; children?: React.ReactNode };
        const match = /language-([\w-]+)/.exec(codeEl.className || '');
        const lang = match ? match[1] : 'text';

        if (lang === 'chart-recharts') {
          try {
            const chartSpec = JSON.parse(String(codeEl.children ?? '').trim()) as MarkdownChartSpec;
            if (!chartSpec || typeof chartSpec !== 'object' || !('type' in chartSpec)) {
              throw new Error('Diagramm-JSON ohne type');
            }
            return <MarkdownRechartsChart chartSpec={chartSpec} />;
          } catch (error) {
            console.error("Fehler beim Parsen der Diagramm-JSON:", error);
            return <div className="text-destructive p-2 my-4 border border-destructive bg-destructive/10 rounded-md">Fehler: Diagrammdaten konnten nicht gelesen werden. Bitte JSON pruefen.</div>;
          }
        }

        const codeText = String(codeEl.children ?? '').replace(/\n$/, '');
        return <CodeBlock language={lang} code={codeText} />;
      }
      return <pre {...props}>{children}</pre>;
    },
    code({ className, children, ...props }: React.HTMLAttributes<HTMLElement>) {
      const match = /language-([\w-]+)/.exec(className || '');

      if (match && match[1] === 'chart-recharts') {
        try {
          const chartSpec = JSON.parse(String(children ?? '').trim()) as MarkdownChartSpec;
          if (!chartSpec || typeof chartSpec !== 'object' || !('type' in chartSpec)) {
            throw new Error('Diagramm-JSON ohne type');
          }
          return <MarkdownRechartsChart chartSpec={chartSpec} />;
        } catch (error) {
          console.error("Fehler beim Parsen der Diagramm-JSON:", error);
          return <div className="text-destructive p-2 my-4 border border-destructive bg-destructive/10 rounded-md">Fehler: Diagrammdaten konnten nicht gelesen werden. Bitte JSON prüfen.</div>;
        }
      }

      return <code className={cn("text-sm", className)} {...props}>{children}</code>;
    },
    table: ({node, ...props}) => (
      <ResponsiveTable {...props} />
    ),
    blockquote: ({ node, children, ...props }) => {
      const getHastText = (n: unknown): string => {
        if (!n || typeof n !== 'object') return '';
        const obj = n as { type?: string; value?: string; children?: unknown[] };
        if (obj.type === 'text') return obj.value || '';
        if (Array.isArray(obj.children)) return obj.children.map(getHastText).join('');
        return '';
      };
      const fullText = getHastText(node).trim();
      const type = isCalloutMarker(fullText);
      if (type) {
        const cleaned = stripCalloutMarker(fullText);
        return <Callout type={type}>{cleaned}</Callout>;
      }
      return <blockquote {...props}>{children}</blockquote>;
    },
    a: ({ href, children, ...props }) => {
      const isExternal = /^https?:\/\//i.test(href || '');
      if (isExternal) {
        return (
          <a href={href} target="_blank" rel="noopener noreferrer" {...props}>
            {children}
          </a>
        );
      }
      return <a href={href} {...props}>{children}</a>;
    },
  };

  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={components}
    >
      {content}
    </ReactMarkdown>
  );
}
